/* eslint-disable react-hooks/exhaustive-deps */

import { useCallback, useEffect, useRef, FC } from 'react';
import { createPortal } from 'react-dom';
import { animated } from 'react-spring';
import { useInView } from 'react-intersection-observer';
import { useImageSize } from './hooks';
import ZoomControl from './ZoomControl/ZoomControl';
import { GallerySlideProps, GalleryImage, Format } from './Gallery.types';
import { Slide, SlideImageWrapper } from './GallerySlide.styles';
import { useZoom } from './hooks';

type ImageFormat = { srcset?: string; type: Format };

const buildResponsiveImageAttributes = (item: GalleryImage) => {
    const src = item.src;
    let sources;
    if (item.responsiveImgList) {
        const formats = item.responsiveImgList;
        sources = (Object.keys(formats) as Array<keyof typeof formats>).reduce(
            (acc: ImageFormat[], key) => {
                const srcset = formats[key]
                    ?.map(
                        (image: { src: string; width: number }) =>
                            `${image.src} ${image.width}w`
                    )
                    .join(', ');

                return [
                    ...acc,
                    {
                        srcset,
                        type: key
                    }
                ];
            },
            []
        );
    }

    const sizes = item.sizes;

    return {
        src,
        sources,
        sizes
    };
};

// Allow react-spring to update the Slide styling outside
// of the react render lifecycle (performance)
const AnimatedSlideImageWrapper = animated(SlideImageWrapper);

const GallerySlide: FC<GallerySlideProps> = ({
    item,
    allowCropping,
    allowImagePrefetching,
    allowMotion,
    viewportRef,
    viewportWidth,
    viewportHeight,
    controlsPortalRef,
    caption,
    isFirst = false,
    isActive = false,
    isClone = false,
    onSlideClick = () => null,
    onSlideZoom = () => null
}) => {
    const slideRef = useRef<HTMLLIElement | null>(null);
    const slideImageWrapperRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);

    const { imageWidth, imageHeight, imageLoadHandler } =
        useImageSize(imageRef);

    const isZoomEnabled =
        imageWidth > viewportWidth || imageHeight > viewportHeight;

    const {
        touchEventHandlers,
        buildCSSTransform,
        isZoomed,
        controls,
        optimalImageWidthForZoom
    } = useZoom(isZoomEnabled, viewportRef, allowMotion, onSlideClick);

    // We're using this hook (a wrapped intersection observer) to determine
    // if this slide image is (or is nearly) visible within the viewport container. By
    // specifying an additional margin we are flagged as visible if we're the next or
    // previous slide too (i.e. we'll be ready and loaded for when we really are visible)
    const [inViewSlideRef, isImageNearlyVisible] = useInView({
        root: viewportRef.current,
        rootMargin: '10px'
    });

    // Conversely we use a similar technique for the reverse - this tells us when the
    // slide is 10px away from leaving the viewport
    const [inViewSlideRef2, isSlideNowOffscreen] = useInView({
        root: viewportRef.current,
        rootMargin: '-10px'
    });

    // Ensure the slide is zoomed out if the content changes or it leaves the viewport
    useEffect(() => {
        controls.zoomOut();
    }, [item, isSlideNowOffscreen]);

    // Just in case the browser doesn't support intersection observer, we always
    // render the image if we're the current slide. We always render cloned slides
    // to avoid any display issues from jumping the viewport from one end to the other,
    // which would otherwise lead to slight lag in rendering the cloned image
    const shouldRenderImage =
        isActive ||
        (allowImagePrefetching && (isImageNearlyVisible || isClone));

    const imageAspectRatio = imageWidth / imageHeight;
    const isLandscapeImage = imageAspectRatio > 1;
    const viewportAspectRatio = viewportWidth / viewportHeight;
    let isScaledAlongHorizontalAxis = imageAspectRatio > viewportAspectRatio;

    if (allowCropping) {
        isScaledAlongHorizontalAxis =
            !isScaledAlongHorizontalAxis && isLandscapeImage;
    }

    // Because we don't know how large the image will be until it's actually loaded, it's
    // a better UX to hide until it's loaded to prevent the layout shift. There's an exception
    // for the first image as we want to be able to preload this
    const isImageLoaded = Boolean(imageWidth && imageHeight);
    const shouldHideImageBeforeFullyLoaded = !isFirst && !isImageLoaded;

    // Fire callback for zoom events
    useEffect(() => {
        isZoomed && onSlideZoom();
    }, [isZoomed]);

    // Build responsive image attributes
    const image = buildResponsiveImageAttributes(item);

    // Potentially switch to higher resolution asset where available
    if (isZoomed && optimalImageWidthForZoom > 0) {
        image.sizes = `${optimalImageWidthForZoom}px`;
    }

    // We're re-using the ref from the useInView hook
    // See: https://github.com/thebuilder/react-intersection-observer#how-can-i-assign-multiple-refs-to-a-component
    const slideRefCallback = useCallback(
        (node) => {
            slideRef.current = node;
            inViewSlideRef(node);
            inViewSlideRef2(node);
        },
        [inViewSlideRef]
    );

    const shouldShowZoomControls = isActive && isImageLoaded && isZoomEnabled;

    return (
        <>
            <Slide
                ref={slideRefCallback}
                allowCropping={allowCropping}
                maxImageDimensions={[imageWidth, imageHeight]}
                isImageLoaded={isImageLoaded}
                isZoomed={isZoomed}
                isScaledAlongHorizontalAxis={isScaledAlongHorizontalAxis}
                aria-hidden={isClone || !isActive}
                data-testid={isClone ? null : 'gallery-image'}
                {...touchEventHandlers()}
            >
                <AnimatedSlideImageWrapper
                    ref={slideImageWrapperRef}
                    data-testid="gallery-image-slide-wrapper"
                    style={{ transform: buildCSSTransform() }}
                >
                    {/* No need to render the image if it's not going to be visible now or during the next interaction */}
                    {shouldRenderImage && (
                        <picture>
                            {image.sources?.map((source) => (
                                <source
                                    srcSet={source.srcset}
                                    type={
                                        source.type === 'original'
                                            ? undefined
                                            : `image/${source.type}`
                                    }
                                    sizes={image.sizes}
                                    key={source.type}
                                />
                            ))}
                            <img
                                ref={imageRef}
                                src={image.src}
                                role={isClone ? 'presentation' : 'img'}
                                alt={caption}
                                draggable="false"
                                onLoad={imageLoadHandler}
                                style={
                                    shouldHideImageBeforeFullyLoaded
                                        ? { opacity: 0 }
                                        : undefined
                                }
                            />
                        </picture>
                    )}
                </AnimatedSlideImageWrapper>
            </Slide>

            {shouldShowZoomControls &&
                controlsPortalRef.current &&
                createPortal(
                    <ZoomControl
                        isZoomInEnabled={!isZoomed}
                        isZoomOutEnabled={isZoomed}
                        onZoomIn={() => controls.zoomIn(true)}
                        onZoomOut={() => controls.zoomOut(true)}
                    ></ZoomControl>,
                    controlsPortalRef.current
                )}
        </>
    );
};

export default GallerySlide;
